import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Box as MuiBox } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import StorefrontIcon from "@mui/icons-material/Storefront";
import InboxIcon from "@mui/icons-material/Inbox";
import SendIcon from "@mui/icons-material/Send";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EventIcon from "@mui/icons-material/Event";
import { axiosClient, axiosClientReports } from "../AxiosClient";

const ButtonSearch = styled(Button)({
    borderRadius: "12px",
    padding: "10px 24px",
    backgroundColor: "#161e55",
    color: "#ffff",
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "0 2px 8px rgba(22, 30, 85, 0.25)",
    "&:hover": {
        backgroundColor: "#2a3580",
        boxShadow: "0 4px 12px rgba(22, 30, 85, 0.35)",
    },
    "&:disabled": {
        backgroundColor: "#9e9e9e",
        color: "#fff",
    },
});

const TextMessage = styled(FormHelperText)({
    color: "red",
});

/** Mapeo mes (nombre inglés) a número para yearMonth yyyy-MM */
const MONTH_TO_MM = {
    January: "01", February: "02", March: "03", April: "04", May: "05", June: "06",
    July: "07", August: "08", September: "09", October: "10", November: "11", December: "12",
};

const months = [
    { value: "January", label: "Enero" },
    { value: "February", label: "Febrero" },
    { value: "March", label: "Marzo" },
    { value: "April", label: "Abril" },
    { value: "May", label: "Mayo" },
    { value: "June", label: "Junio" },
    { value: "July", label: "Julio" },
    { value: "August", label: "Agosto" },
    { value: "September", label: "Septiembre" },
    { value: "October", label: "Octubre" },
    { value: "November", label: "Noviembre" },
    { value: "December", label: "Diciembre" },
];

const COLUMNS_RESUMEN = [
    "Nombre",
    "Old ID",
    "Correo",
    "Valor desembolso",
];

export default function ValidacionInformes() {
    const [mes, setMes] = useState("");
    const [year, setYear] = useState("");
    const [selectedCommerces, setSelectedCommerces] = useState([]);
    const [selectedSendDate, setSelectedSendDate] = useState("");
    const [commerces, setCommerces] = useState([]);
    const [errorMes, setErrorMes] = useState(false);
    const [errorYear, setErrorYear] = useState(false);
    const [datosValidacion, setDatosValidacion] = useState([]);
    const [loading, setLoading] = useState(false);
    const [consultado, setConsultado] = useState(false);
    const [selectedForSend, setSelectedForSend] = useState(new Set());
    const [modalConfirmOpen, setModalConfirmOpen] = useState(false);
    const [sendingEmails, setSendingEmails] = useState(false);
    const [apiError, setApiError] = useState(null);

    const getFechaEnvio = (row) => row.lastSentDate ?? row.fechaEnvio ?? row.fecha_envio ?? row.sentAt ?? "";

    const getFechaEnvioKey = (row) => {
        const rawDate = getFechaEnvio(row);
        if (rawDate == null || String(rawDate).trim() === "") return "";
        const str = String(rawDate).trim();
        const localDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
        if (localDateMatch) return `${localDateMatch[1]}-${localDateMatch[2]}-${localDateMatch[3]}`;
        const parsedDate = new Date(str);
        if (isNaN(parsedDate.getTime())) return "";
        return [
            parsedDate.getFullYear(),
            String(parsedDate.getMonth() + 1).padStart(2, "0"),
            String(parsedDate.getDate()).padStart(2, "0"),
        ].join("-");
    };

    const sendDateOptions = useMemo(() => {
        const uniqueDates = new Set();
        datosValidacion.forEach((row) => {
            const dateKey = getFechaEnvioKey(row);
            if (dateKey) uniqueDates.add(dateKey);
        });
        return Array.from(uniqueDates).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    }, [datosValidacion]);

    const displayData = useMemo(() => {
        if (!consultado || datosValidacion.length === 0) return [];
        let filtered = datosValidacion;

        if (selectedCommerces.length > 0) {
            const ids = new Set(selectedCommerces.map((c) => c.oldId));
            filtered = filtered.filter((row) => ids.has(row.oldId ?? row.old_id));
        }

        if (selectedSendDate === "__EMPTY__") {
            filtered = filtered.filter((row) => getFechaEnvioKey(row) === "");
        } else if (selectedSendDate) {
            filtered = filtered.filter((row) => getFechaEnvioKey(row) === selectedSendDate);
        }

        return filtered;
    }, [consultado, datosValidacion, selectedCommerces, selectedSendDate]);

    const getBackendErrorMessage = (err) => {
        const data = err.response?.data;
        if (!data) return err.message || "Error al cargar los datos.";
        if (typeof data === "string") return data;
        return data.message ?? data.error ?? data.detail ?? JSON.stringify(data);
    };

    const handleChangeMes = (event) => setMes(event.target.value);
    const handleChangeYear = (event) => setYear(event.target.value);

    const getCommerces = () => {
        axiosClient
            .get("/v1/commerces/all-commerces")
            .then((response) => {
                if (response.status === 200) {
                    const sorted = response.data.sort((a, b) => a.oldId - b.oldId);
                    setCommerces(sorted);
                }
            })
            .catch((error) => {
                console.log("Bad request: " + error.message);
            });
    };

    const consultarPorFecha = () => {
        setLoading(true);
        setConsultado(true);
        setSelectedForSend(new Set());
        setSelectedSendDate("");
        setApiError(null);
        const yearMonth = `${year}-${MONTH_TO_MM[mes] || "01"}`;
        axiosClient
            .get("/v1/commerceReportController", { params: { yearMonth } })
            .then((response) => {
                setApiError(null);
                if (response.data && Array.isArray(response.data)) {
                    setDatosValidacion(
                        response.data.map((item) => {
                            const c = item.commerce || {};
                            return {
                                ...c,
                                name: c.name ?? c.nombre,
                                nombre: c.name ?? c.nombre,
                                oldId: c.oldId ?? c.old_id,
                                old_id: c.oldId ?? c.old_id,
                                email: c.email ?? c.correo,
                                correo: c.email ?? c.correo,
                                valorDesembolso: item.totalToDisburse,
                                valor_desembolso: item.totalToDisburse,
                                totalCredits: item.totalCredits,
                                lastSentDate: item.lastSentDate,
                                fechaEnvio: item.lastSentDate,
                                fecha_envio: item.lastSentDate,
                            };
                        })
                    );
                } else {
                    setDatosValidacion([]);
                }
            })
            .catch((err) => {
                setDatosValidacion([]);
                setApiError(getBackendErrorMessage(err));
            })
            .finally(() => setLoading(false));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setErrorMes(false);
        setErrorYear(false);

        if (mes === "") setErrorMes(true);
        if (year === "" || isNaN(year)) setErrorYear(true);

        if (mes && year) {
            consultarPorFecha();
        }
    };

    const formatearMoneda = (valor) => {
        if (valor == null || valor === "") return "-";
        const num = Number(valor);
        if (isNaN(num)) return valor;
        return new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(num);
    };

    /** Formatea solo fecha (LocalDate YYYY-MM-DD), sin hora. Parsea como fecha local para evitar desfase por UTC. */
    const formatearSoloFecha = (fecha) => {
        if (fecha == null || fecha === "") return "-";
        try {
            const str = String(fecha).trim();
            const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
            const d = match
                ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
                : new Date(fecha);
            if (isNaN(d.getTime())) return "-";
            return d.toLocaleDateString("es-CO", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        } catch {
            return "-";
        }
    };

    const getRowId = (row, index) => row.oldId ?? row.old_id ?? `idx-${index}`;

    const handleDownloadExcel = (row) => {
        const commerceId = row.oldId ?? row.old_id;
        if (!mes || !year || commerceId == null) return;
        const url = `/reports/commerces/excel/${commerceId}/${mes}/${year}`;
        axiosClientReports
            .get(url, { responseType: "blob" })
            .then((response) => {
                const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = blobUrl;
                const name = (row.nombre ?? row.name ?? "comercio").replace(/\s+/g, "_");
                link.setAttribute("download", `${name}_${mes}_${year}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch((err) => {
                const message =
                    err.response?.status === 404
                        ? "No se encontró el reporte para este comercio o periodo."
                        : err.message || "Error al descargar el Excel.";
                window.alert(message);
            });
    };

    const handleDownloadPdf = (row) => {
        const oldId = row.oldId ?? row.old_id;
        if (!mes || !year || oldId == null) return;
        axiosClient
            .get(`/v1/commerceReportController/pdf/${oldId}/${encodeURIComponent(mes)}/${year}`, { responseType: "blob" })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                const name = (row.nombre ?? row.name ?? "comercio").replace(/\s+/g, "_");
                link.setAttribute("download", `${name}_${mes}_${year}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => {
                const status = err.response?.status;
                const message =
                    status === 404
                        ? "No se encontró el comercio o no existe reporte para este periodo. Verifique que el comercio exista en el backend y que haya datos para el mes y año seleccionados."
                        : err.response?.data instanceof Blob
                            ? "Error al generar el PDF. Intente de nuevo."
                            : err.message || "Error al descargar el PDF.";
                window.alert(message);
            });
    };

    const handleToggleSelectRow = (rowId) => {
        setSelectedForSend((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    };

    const handleSelectAll = (checked) => {
        if (checked) setSelectedForSend(new Set(displayData.map((row, i) => getRowId(row, i))));
        else setSelectedForSend(new Set());
    };

    const allSelected = displayData.length > 0 && selectedForSend.size === displayData.length;
    const someSelected = selectedForSend.size > 0;

    const handleOpenConfirmModal = () => {
        if (selectedForSend.size > 0) setModalConfirmOpen(true);
    };

    const handleCloseConfirmModal = () => setModalConfirmOpen(false);

    const handleConfirmEnvio = () => {
        if (!mes || !year || selectedForSend.size === 0) {
            handleCloseConfirmModal();
            return;
        }
        const yearMonth = `${year}-${MONTH_TO_MM[mes] || "01"}`;
        const commerceIds = displayData
            .filter((row, i) => selectedForSend.has(getRowId(row, i)))
            .map((row) => row.oldId ?? row.old_id)
            .filter((id) => id != null);
        setSendingEmails(true);
        axiosClient
            .post("/v1/commerceReportController/send-email-report", { yearMonth, commerceIds })
            .then(() => {
                handleCloseConfirmModal();
                setSelectedForSend(new Set());
                window.alert("Informes enviados correctamente por correo a los comercios seleccionados.");
            })
            .catch((err) => {
                const msg = err.response?.data?.message || err.message || "Error al enviar los informes. Intente de nuevo.";
                window.alert(msg);
            })
            .finally(() => setSendingEmails(false));
    };

    useEffect(() => {
        if (localStorage.getItem("userUuid") == null) {
            window.location = "/admin";
        }
        getCommerces();
    }, []);

    return (
        <React.Fragment>
            <MuiBox
                sx={{
                    width: "100%",
                    minHeight: "100vh",
                    marginTop: { xs: 1, sm: "1.5rem" },
                    px: { xs: 1.5, sm: 2, md: 3 },
                    pb: { xs: 2, sm: 4 },
                }}
            >
                <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                    {/* Header */}
                    <Box
                        sx={{
                            mb: { xs: 2, sm: 3 },
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1, sm: 1.5 },
                            flexWrap: "wrap",
                        }}
                    >
                        <FactCheckIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: "#161e55" }} />
                        <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
                            <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
                                Validación de informes
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Revisa los datos antes de enviar los informes por correo
                            </Typography>
                        </Box>
                    </Box>

                    {/* Filtros en card */}
                    <Card
                        elevation={0}
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: { xs: 1.5, sm: 2 },
                            mb: { xs: 2, sm: 3 },
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }} component="form" onSubmit={handleSubmit}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                1. Período (obligatorio)
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small" error={errorMes}>
                                        <InputLabel id="mes">Mes</InputLabel>
                                        <Select
                                            labelId="mes"
                                            label="Mes"
                                            value={mes}
                                            onChange={handleChangeMes}
                                        >
                                            {months.map((m) => (
                                                <MenuItem key={m.value} value={m.value}>
                                                    {m.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errorMes && <TextMessage>Selecciona el mes</TextMessage>}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Año"
                                        placeholder="Ej: 2025"
                                        value={year}
                                        onChange={handleChangeYear}
                                        error={errorYear}
                                    />
                                    {errorYear && (
                                        <TextMessage>Ingresa un año válido (número)</TextMessage>
                                    )}
                                </Grid>
                            </Grid>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 2, mb: 1 }}>
                                <StorefrontIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                <Typography variant="subtitle1" fontWeight={600}>
                                    2. Comercio (opcional)
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                                Puedes elegir varios comercios. Si no eliges ninguno, se muestran todos para el período seleccionado.
                            </Typography>
                            <Autocomplete
                                multiple
                                size="small"
                                options={commerces}
                                value={selectedCommerces}
                                onChange={(_, newValue) => setSelectedCommerces(newValue)}
                                getOptionLabel={(option) =>
                                    typeof option === "object" && option
                                        ? `${option.oldId}. ${option.name}`
                                        : ""
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option?.oldId === value?.oldId
                                }
                                filterOptions={(options, { inputValue }) =>
                                    options.filter((c) => {
                                        const search = inputValue.toLowerCase();
                                        return (
                                            String(c.oldId).toLowerCase().includes(search) ||
                                            (c.name && c.name.toLowerCase().includes(search))
                                        );
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Buscar por nombre o ID"
                                        placeholder="Escribe para filtrar o elegir varios..."
                                    />
                                )}
                                ListboxProps={{
                                    style: { maxHeight: 280 },
                                }}
                                noOptionsText="Ningún comercio coincide"
                                sx={{ mb: 2 }}
                            />

                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, mb: 1 }}>
                                <EventIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                <Typography variant="subtitle1" fontWeight={600}>
                                    3. Fecha de envío (opcional)
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                                Puedes filtrar por una fecha específica o por comercios sin fecha de envío.
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel id="send-date-filter">Fecha de envío</InputLabel>
                                <Select
                                    labelId="send-date-filter"
                                    label="Fecha de envío"
                                    value={selectedSendDate}
                                    onChange={(event) => setSelectedSendDate(event.target.value)}
                                    disabled={!consultado || datosValidacion.length === 0}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    <MenuItem value="__EMPTY__">Sin fecha de envío</MenuItem>
                                    {sendDateOptions.map((dateKey) => (
                                        <MenuItem key={dateKey} value={dateKey}>
                                            {formatearSoloFecha(dateKey)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <ButtonSearch
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                type="submit"
                                disabled={loading}
                                sx={{ mt: 1 }}
                            >
                                {loading ? "Consultando..." : "Consultar"}
                            </ButtonSearch>
                        </CardContent>
                    </Card>

                    {/* Resultados */}
                    <Card
                        elevation={0}
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: { xs: 1.5, sm: 2 },
                            overflow: "hidden",
                            width: "100%",
                        }}
                    >
                        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
                            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "inherit" } }}>
                                Resultados
                                {!loading && (
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                        {apiError
                                            ? "— Error"
                                            : `— ${displayData.length} ${displayData.length === 1 ? "registro" : "registros"}`}
                                    </Typography>
                                )}
                            </Typography>
                        </Box>
                        <TableContainer
                            sx={{
                                maxHeight: { xs: 360, sm: 440 },
                                overflowX: "auto",
                                overflowY: "auto",
                            }}
                        >
                            <Table
                                size="small"
                                stickyHeader
                                sx={{ minWidth: 720 }}
                            >
                                <TableHead>
                                    <TableRow>
                                        {COLUMNS_RESUMEN.map((col) => (
                                            <TableCell
                                                key={col}
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    backgroundColor: "#161e55",
                                                    color: "#fff",
                                                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                                    whiteSpace: "nowrap",
                                                    px: { xs: 1, sm: 2 },
                                                }}
                                            >
                                                {col}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: "#161e55", color: "#fff", fontSize: { xs: "0.7rem", sm: "0.75rem" }, whiteSpace: "nowrap", px: 1 }}>Fecha envío</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: "#161e55", color: "#fff", fontSize: { xs: "0.7rem", sm: "0.75rem" }, whiteSpace: "nowrap", px: 1 }}>Excel</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: "#161e55", color: "#fff", fontSize: { xs: "0.7rem", sm: "0.75rem" }, whiteSpace: "nowrap", px: 1 }}>PDF</TableCell>
                                        <TableCell
                                            align="center"
                                            padding="checkbox"
                                            sx={{
                                                fontWeight: 600,
                                                backgroundColor: "#161e55",
                                                color: "#fff",
                                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                                whiteSpace: "nowrap",
                                                position: "sticky",
                                                right: 0,
                                                zIndex: 3,
                                                boxShadow: "-4px 0 8px rgba(0,0,0,0.1)",
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ display: { xs: "none", sm: "inline" } }}>Envío masivo</Typography>
                                            <Checkbox
                                                size="small"
                                                checked={allSelected}
                                                indeterminate={someSelected && !allSelected}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                sx={{ color: "#fff", py: 0, "&.Mui-checked": { color: "#fff" }, "&.MuiCheckbox-indeterminate": { color: "#fff" } }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={COLUMNS_RESUMEN.length + 4} align="center" sx={{ py: 4 }}>
                                                <CircularProgress size={32} sx={{ color: "#161e55" }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    Cargando...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={COLUMNS_RESUMEN.length + 4} align="center" sx={{ py: 5 }}>
                                                <InboxIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                                                {apiError ? (
                                                    <>
                                                        <Typography color="error" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                            Error al cargar los datos
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {apiError}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Typography color="text.secondary">
                                                            No hay datos para los filtros seleccionados
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Prueba otro período o ajusta el filtro de comercios
                                                        </Typography>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((row, index) => {
                                            const rowId = getRowId(row, index);
                                            return (
                                                <TableRow
                                                    key={rowId}
                                                    sx={{
                                                        "&:nth-of-type(even)": { backgroundColor: "grey.50" },
                                                        "&:hover": { backgroundColor: "action.hover" },
                                                    }}
                                                >
                                                    <TableCell align="center" sx={{ fontWeight: 500, fontSize: { xs: "0.75rem", sm: "inherit" }, px: { xs: 1, sm: 2 } }}>
                                                        {row.nombre ?? row.name ?? "-"}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "inherit" }, px: { xs: 1, sm: 2 } }}>{row.oldId ?? row.old_id ?? "-"}</TableCell>
                                                    <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "inherit" }, px: { xs: 1, sm: 2 } }}>{row.correo ?? row.email ?? "-"}</TableCell>
                                                    <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "inherit" }, px: { xs: 1, sm: 2 } }}>
                                                        {formatearMoneda(row.valorDesembolso ?? row.valor_desembolso)}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: { xs: "0.75rem", sm: "inherit" }, px: { xs: 1, sm: 2 } }}>
                                                        {getFechaEnvio(row) ? (
                                                            <Tooltip title={formatearSoloFecha(getFechaEnvio(row))}>
                                                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                                                    <EventIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                                                    {formatearSoloFecha(getFechaEnvio(row))}
                                                                </Box>
                                                            </Tooltip>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ px: 0.5 }}>
                                                        <Tooltip title="Descargar Excel">
                                                            <IconButton size="small" onClick={() => handleDownloadExcel(row)} sx={{ color: "#161e55" }} aria-label="Descargar Excel">
                                                                <TableViewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ px: 0.5 }}>
                                                        <Tooltip title="Descargar PDF">
                                                            <IconButton size="small" onClick={() => handleDownloadPdf(row)} sx={{ color: "#d32f2f" }} aria-label="Descargar PDF">
                                                                <PictureAsPdfIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        padding="checkbox"
                                                        sx={{
                                                            position: "sticky",
                                                            right: 0,
                                                            backgroundColor: "inherit",
                                                            zIndex: 1,
                                                            boxShadow: "-4px 0 8px rgba(0,0,0,0.06)",
                                                        }}
                                                    >
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedForSend.has(rowId)}
                                                            onChange={() => handleToggleSelectRow(rowId)}
                                                            sx={{ color: "#161e55", "&.Mui-checked": { color: "#161e55" } }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2 }}>
                            {someSelected && (
                                <Typography variant="body2" color="text.secondary">
                                    {selectedForSend.size} {selectedForSend.size === 1 ? "comercio seleccionado" : "comercios seleccionados"}
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<SendIcon />}
                                onClick={handleOpenConfirmModal}
                                disabled={!someSelected}
                                sx={{
                                    borderRadius: "12px",
                                    padding: "10px 24px",
                                    backgroundColor: "#161e55",
                                    color: "#fff",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    "&:hover": { backgroundColor: "#2a3580" },
                                    "&:disabled": { backgroundColor: "#9e9e9e", color: "#fff" },
                                }}
                            >
                                Enviar informes
                            </Button>
                        </Box>
                    </Card>

                    <Dialog open={modalConfirmOpen} onClose={handleCloseConfirmModal} maxWidth="sm" fullWidth>
                        <DialogTitle>Confirmar envío</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                ¿Desea enviar los informes a los {selectedForSend.size} {selectedForSend.size === 1 ? "comercio seleccionado" : "comercios seleccionados"}? Se enviará la información por correo a cada uno.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={handleCloseConfirmModal} color="inherit" sx={{ textTransform: "none" }} disabled={sendingEmails}>
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleConfirmEnvio}
                                variant="contained"
                                disabled={sendingEmails}
                                startIcon={sendingEmails ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{
                                    backgroundColor: "#161e55",
                                    textTransform: "none",
                                    "&:hover": { backgroundColor: "#2a3580" },
                                }}
                            >
                                {sendingEmails ? "Enviando..." : "Confirmar envío"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </MuiBox>
        </React.Fragment>
    );
}
